<template>
    <PageSidebar>
        <template #sidebarTitle>
            <div class="flex items-center space-x-3">
                <ButtonSecondaryIconSm @click="quit">
                    <ArrowLeftIcon />
                </ButtonSecondaryIconSm>
                <span class="flex-grow">{{ event.title }} </span>
                <FormToggleSm v-model="tickerActive" />
            </div>
        </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <FormTextArea
                    :title="$t('ticker.event.title')"
                    v-model="tickerText"
                    id="tickerTitle"
                />
                <div class="space-y-2">
                    <FormLabel>{{ $t("ticker.event.use_variables") }}</FormLabel>
                    <ol class="text-sm ct-text-3 space-y-1 list-inside list-disc">
                        <li>
                            <BadgeGrayLg>{product_name}</BadgeGrayLg> - {{ $t("shared.eg") }}
                            {{ $t("ticker.examples.product_name") }}
                        </li>
                        <li>
                            <BadgeGrayLg>{price}</BadgeGrayLg> - {{ $t("shared.eg") }}
                            {{ $t("ticker.examples.price") }}
                        </li>
                        <li>
                            <BadgeGrayLg>{customer_name}</BadgeGrayLg> - {{ $t("shared.eg") }}
                            {{ $t("ticker.examples.customer_name") }}
                        </li>
                        <li>
                            <BadgeGrayLg>{city}</BadgeGrayLg> - {{ $t("shared.eg") }}
                            {{ $t("ticker.examples.city") }}
                        </li>
                        <li>
                            <BadgeGrayLg>{country_code}</BadgeGrayLg> -
                            {{ $t("ticker.event.flag") }} - {{ $t("shared.eg") }}
                            <span
                                :class="`inline-block fi fi-${$t('ticker.examples.country_code').toLowerCase()}`"
                            ></span>
                        </li>
                    </ol>
                </div>

                <div class="relative space-y-2" id="tickerIcon">
                    <FormLabel>
                        <span>{{ $t("ticker.event.icon") }}</span>
                    </FormLabel>
                    <div
                        @click="toggleIcons"
                        class="ct-input"
                        :class="[showIcons ? 'ct-ring-focus !ring-2' : '']"
                    >
                        <div
                            class="cursor-pointer flex space-x-1 items-center justify-between w-full"
                        >
                            <CardIcon>
                                <component :is="currentIconComponent" />
                            </CardIcon>

                            <ChevronDownIcon
                                :class="[showIcons ? 'rotate-180' : 'rotate-0']"
                                class="transition-transform flex-shrink-0 h-5 w-5 text-gray-400"
                            />
                        </div>

                        <div
                            v-click-outside="hideIcons"
                            v-if="showIcons"
                            class="w-full absolute -bottom-1 left-0 translate-y-full z-10"
                        >
                            <DropdownList
                                class="w-full max-h-60 overflow-auto grid gap-2 grid-cols-6 p-2"
                            >
                                <DropdownItem
                                    @click="setIcon(icon)"
                                    v-for="icon in icons"
                                    :key="icon.name"
                                    :class="[
                                        icon.name === tickerIcon
                                            ? 'ring-1 ring-inset ct-ring-brand rounded-md'
                                            : '',
                                    ]"
                                >
                                    <component :is="icon.icon" class="w-6 h-6" />
                                </DropdownItem>
                            </DropdownList>
                        </div>
                    </div>
                </div>
                <FormFiles
                    v-model="files"
                    :title="$t('ticker.event.image')"
                    accept="image/*"
                    :labels="{
                        clickToUpload: $t('shared.uploader.click_to_upload'),
                        dragAndDrop: $t('shared.uploader.drag_and_drop'),
                        fileInfo: $t('shared.uploader.file_details'),
                    }"
                    :maxSize="10000"
                    :errorMessages="{
                        invalidSize: $t('shared.invalid.file_size_over'),
                        invalidType: $t('shared.invalid.file_type'),
                    }"
                    :single-file="true"
                    @fileSelected="uploadImg"
                    @fileRemoved="removeImg"
                />
                <ButtonSecondaryMd v-if="eventHasChanges" class="w-full" @click="revertToDefaults">
                    <ArrowReloadIcon />
                    <span>{{ $t("ticker.event.revert_to_default") }}</span>
                </ButtonSecondaryMd>
            </div>
        </template>
        <template #sidebarFooter>
            <cSaveButton
                @save="handleFormSave"
                :disabled="!isDirty"
                :ctaDisabled="$t('shared.save')"
            />
        </template>
    </PageSidebar>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { PageSidebar } from "@easy/ui/page-components";
import { FormTextArea, FormLabel, FormFiles, makeFileModel, FormToggleSm } from "@easy/ui/forms";
import { BadgeGrayLg } from "@easy/ui/badges";
import { ArrowReloadIcon, ChevronDownIcon, ArrowLeftIcon } from "@easy/ui/icons";
import { ButtonSecondaryIconSm, ButtonSecondaryMd } from "@easy/ui/buttons";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { useSaveOrDropModal } from "@/store/useSaveOrDropModal";
import { useIsDirty } from "@/shared/utils/forms/useIsDirty";
import { DropdownList, DropdownItem } from "@easy/ui/dropdowns";
import { CardIcon } from "@easy/ui/cards";
import { tickersService } from "@/apps/ticker/services/tickers.service";
import { useFocusOnQueryField } from "@/shared/utils/forms/useFocusOnQueryField";

export default {
    components: {
        PageSidebar,
        FormTextArea,
        FormLabel,
        ArrowLeftIcon,
        ButtonSecondaryIconSm,
        cSaveButton,
        DropdownList,
        DropdownItem,
        ChevronDownIcon,
        CardIcon,
        FormFiles,
        ButtonSecondaryMd,
        FormToggleSm,
        ArrowReloadIcon,
        BadgeGrayLg,
    },
    props: {
        event: {
            type: Object,
            required: true,
        },
        tickerEvent: {
            type: Object,
            required: true,
        },
        backupData: {
            type: Object,
            required: true,
        },
    },
    emits: ["cancel", "save"],
    setup(props) {
        useFocusOnQueryField();
        const { isDirty } = useIsDirty(props.backupData, props.tickerEvent);
        const { askToConfirmIfNeeded } = useSaveOrDropModal();

        return {
            isDirty,
            askToConfirmIfNeeded,
        };
    },
    data() {
        return {
            showIcons: false,
            icons: tickersService.getIconsList(),
            files: [],
        };
    },
    watch: {
        tickerEvent: {
            handler(tickerEvent, oldTickerEvent) {
                if (!oldTickerEvent?.image && tickerEvent?.image) {
                    this.files = [
                        makeFileModel({
                            url: tickerEvent.image,
                        }),
                    ];
                }
            },
            immediate: true,
            deep: true,
        },
    },
    computed: {
        ...mapState(["lang"]),
        ...mapState("ticker", ["ticker"]),
        eventHasChanges() {
            return this.tickerEvent.icon || this.tickerEvent.text;
        },
        tickerIcon() {
            return this.tickerEvent.icon || this.event.icon;
        },
        tickerActive: {
            get() {
                return this.tickerEvent ? this.tickerEvent.active : false;
            },
            set(value) {
                this.tickerEvent.active = value;
            },
        },
        tickerText: {
            get() {
                return this.tickerEvent?.text || this.event.description;
            },
            set(value) {
                this.tickerEvent.text = value;
            },
        },
        currentIconComponent() {
            return this.icons.find((icon) => icon.name === this.tickerIcon).icon;
        },
    },
    methods: {
        ...mapActions("ticker", ["readImageFile"]),
        async quit() {
            const result = await this.askToConfirmIfNeeded({
                showModal: this.isDirty,
                onConfirmChanges: this.handleFormSave,
            });
            if (!result) {
                this.$emit("cancel");
            }
        },
        async handleFormSave() {
            if (!this.tickerEvent._created) {
                this.tickerEvent["_changed"] = true;
            }
            this.$emit("save");
        },
        toggleIcons() {
            this.showIcons = !this.showIcons;
        },
        hideIcons() {
            this.showIcons = false;
        },
        setIcon(icon) {
            this.tickerEvent.icon = icon.name;
            this.hideIcons();
        },
        async uploadImg(file) {
            const image = await this.readImageFile({ file });
            this.tickerEvent.image = image;
        },
        removeImg() {
            this.tickerEvent.image = "";
        },
        async revertToDefaults() {
            this.tickerEvent.text = "";
            this.tickerEvent.icon = "";
            this.tickerEvent.image = "";
        },
    },
};
</script>
